/**
 * @module lib/authToken
 * @description The one place EmberPOS keeps its auth tokens (multi-tenant spec D2/D7).
 *
 * - Device token: the owner JWT from /api/tenants/login|register. Binds this
 *   device to a restaurant; PIN login sends it. Key `fire-pos_token` (the key
 *   SubscriptionGate always used). Older builds of OnboardingFlow/AdminDashboard
 *   wrote `fire-pos-token` (hyphen); it is migrated on first read.
 * - Staff token: the 12 h staff JWT returned by PIN login. Cleared on POS logout.
 *
 * Every accessor swallows storage errors (private mode, blocked storage).
 */

export const DEVICE_TOKEN_KEY = 'fire-pos_token';
export const LEGACY_DEVICE_TOKEN_KEY = 'fire-pos-token';
export const STAFF_TOKEN_KEY = 'fire-pos_staff_token';

function read(key) {
    try { return localStorage.getItem(key) || null; } catch { return null; }
}
function write(key, value) {
    try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
}
function remove(key) {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
}

export function getDeviceToken() {
    const token = read(DEVICE_TOKEN_KEY);
    if (token) return token;
    const legacy = read(LEGACY_DEVICE_TOKEN_KEY);
    if (!legacy) return null;
    write(DEVICE_TOKEN_KEY, legacy);
    remove(LEGACY_DEVICE_TOKEN_KEY);
    return legacy;
}

export function setDeviceToken(token) {
    if (!token) return;
    write(DEVICE_TOKEN_KEY, token);
    remove(LEGACY_DEVICE_TOKEN_KEY);
}

/** Un-enrolls the device: drops the device token and any staff session bound to it. */
export function clearDeviceToken() {
    remove(DEVICE_TOKEN_KEY);
    remove(LEGACY_DEVICE_TOKEN_KEY);
    remove(STAFF_TOKEN_KEY);
}

export function getStaffToken() {
    return read(STAFF_TOKEN_KEY);
}

export function setStaffToken(token) {
    if (token) write(STAFF_TOKEN_KEY, token);
}

export function clearStaffToken() {
    remove(STAFF_TOKEN_KEY);
}

/** Token for general API calls: the staff session if signed in, else the device token. */
export function getRequestToken() {
    return getStaffToken() || getDeviceToken();
}

/**
 * tenantId claim of the request token (decoded, NOT verified: only used to key
 * local caches; the server verifies every request). null with no/garbled token,
 * which the server serves as the sample tenant (spec D3).
 */
export function getTokenTenantId() {
    const token = getRequestToken();
    try {
        const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const id = JSON.parse(atob(part)).tenantId;
        return typeof id === 'string' && id ? id : null;
    } catch {
        return null;
    }
}
